import { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Loader2, RefreshCw, Ticket, AlertTriangle, Download } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../../lib/api';
import { when, IssuedTable, AvailableTable, BilledTable, NotBilledTable, ExpiringTable } from './VoucherTables';

const COLS = [['total', 'In CRM'], ['issued', 'Issued'], ['available', 'Available'], ['billed', 'Billed in Books'], ['billed_not_issued', 'Billed, not issued'], ['issued_not_billed', 'Issued, not billed'], ['expiring', 'Expiring soon'], ['expired_unused', 'Expired unused']];

const Summary = ({ rows }) => (
  <Card className="overflow-x-auto" data-testid="vc-summary">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><tr><th className="text-left px-4 py-2">Exam</th>{COLS.map(([k, l]) => <th key={k} className="text-right px-3">{l}</th>)}</tr></thead>
      <tbody>{rows.map(r => <tr key={r.exam} className="border-t border-slate-100" data-testid={`vc-summary-${r.exam}`}>
        <td className="px-4 py-2 font-semibold text-slate-900">{r.exam}</td>
        {COLS.map(([k]) => <td key={k} className={`text-right px-3 ${k === 'available' ? 'text-emerald-700 font-bold' : ['billed_not_issued', 'issued_not_billed', 'expiring', 'expired_unused'].includes(k) && r[k] ? 'text-amber-700 font-semibold' : 'text-slate-700'}`}>{r[k]}</td>)}
      </tr>)}</tbody>
    </table>
  </Card>
);

const match = (x, t) => !t || JSON.stringify([x.voucher_code, x.student, x.enrollment_no, x.invoice_number, x.customer_name, x.enrolment_field, x.invoice?.invoice_number]).toLowerCase().includes(t);

export default function VoucherPage() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState('');
  const [exam, setExam] = useState('');
  const [from, setFrom] = useState('');
  const [days, setDays] = useState(60);
  const [daysTxt, setDaysTxt] = useState('60');
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('issued');
  const [dl, setDl] = useState(false);
  const download = async (fmt, scope) => {
    setDl(true);
    try {
      const r = await api.get('/vouchers/export', { params: { fmt, tab: scope, exam, q, date_from: from, expiry_days: days }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `vouchers_${scope}_${new Date().toISOString().slice(0, 10)}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); } finally { setDl(false); }
  };
  const load = useCallback(() => api.get('/vouchers/report', { params: { expiry_days: days } }).then(r => { setD(r.data); return r.data; }), [days]);
  useEffect(() => { load(); }, [load]);
  const refresh = async () => {
    setBusy(true);
    const before = d?.meta?.synced_at;
    try {
      await api.post('/vouchers/refresh');
      for (let i = 0; i < 40; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const n = await load();
        if (!n.meta.running && (n.meta.synced_at !== before || n.meta.error)) { n.meta.error ? toast.error(n.meta.error) : toast.success('Voucher data refreshed'); break; }
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Refresh failed'); } finally { setBusy(false); }
  };
  if (!d) return <div className="py-20 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const t = q.trim().toLowerCase();
  const f = rows => (rows || []).filter(x => (!exam || x.exam === exam) && match(x, t) && (!from || (x.date || x.invoice?.date || x.modified_time || '').slice(0, 10) >= from));
  const lists = d.empty ? {} : { issued: f(d.issued), available: f(d.available), billed: f(d.billed_not_issued), notbilled: f(d.issued_not_billed), expiring: f(d.expiring) };
  return (
    <div className="space-y-5" data-testid="voucher-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2"><Ticket className="w-5 h-5 text-violet-600" />Exam Vouchers</h1>
          <p className="text-sm text-slate-500">CRM Voucher module vs Books “Exam Voucher” invoices, matched student → Books customer → voucher invoice (any date), with the invoice's Enrolment No as backup. Vouchers created and invoices dated from FY 2026-27 (1 Apr 2026).</p>
        </div>
        <div className="flex flex-col items-end shrink-0 ml-auto">
          <div className="flex items-center gap-2">
          {!d.empty && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" disabled={dl} data-testid="vc-download">{dl ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}Download</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-[11px] text-slate-500">Uses your current filters</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => download('xlsx', tab)} data-testid="vc-dl-xlsx-tab">Excel — this tab</DropdownMenuItem>
                <DropdownMenuItem onClick={() => download('xlsx', 'all')} data-testid="vc-dl-xlsx-all">Excel — all lists (one sheet each)</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => download('pdf', tab)} data-testid="vc-dl-pdf-tab">PDF — this tab</DropdownMenuItem>
                <DropdownMenuItem onClick={() => download('pdf', 'all')} data-testid="vc-dl-pdf-all">PDF — all lists</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button size="sm" onClick={refresh} disabled={busy || d.meta.running} data-testid="vc-refresh"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy || d.meta.running ? 'animate-spin' : ''}`} />Refresh now</Button>
          </div>
          <div className="text-[11px] text-slate-500 mt-1" data-testid="vc-last-refresh">Last refreshed {when(d.meta.synced_at)}{d.meta.synced_by ? ` · ${d.meta.synced_by}` : ''} · refreshes nightly</div>
        </div>
      </div>
      {d.meta.error && <Card className="p-3 text-xs text-rose-700 bg-rose-50 border-rose-200 flex gap-2" data-testid="vc-error"><AlertTriangle className="w-4 h-4" />Last refresh failed ({when(d.meta.error_at)}): {d.meta.error}</Card>}
      {d.empty ? <Card className="p-10 text-center text-sm text-slate-500" data-testid="vc-empty">No voucher data yet — click Refresh now.</Card> : <>
        <Summary rows={d.summary} />
        <div className="flex flex-wrap items-end gap-2">
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search voucher, student, enrollment, invoice" className="h-8 w-72 text-xs" data-testid="vc-search" />
          <select value={exam} onChange={e => setExam(e.target.value)} className="h-8 border border-slate-200 rounded-md text-xs px-2 bg-white" data-testid="vc-exam-filter">
            <option value="">All exams</option>{d.summary.map(s => <option key={s.exam} value={s.exam}>{s.exam}</option>)}
          </select>
          <label className="text-[11px] text-slate-500">From (invoice date / CRM last change)<Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-8 w-36 text-xs" data-testid="vc-from-date" /></label>
          <label className="text-[11px] text-slate-500">Expiring within (days)<Input type="number" value={daysTxt} min={1} max={365} onChange={e => setDaysTxt(e.target.value)} onBlur={() => { const n = Math.min(365, Math.max(1, Number(daysTxt) || 60)); setDaysTxt(String(n)); setDays(n); }} onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()} className="h-8 w-24 text-xs" data-testid="vc-expiry-days" /></label>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="issued" data-testid="vc-tab-issued">Issued ({lists.issued.length})</TabsTrigger>
            <TabsTrigger value="available" data-testid="vc-tab-available">Available stock ({lists.available.length})</TabsTrigger>
            <TabsTrigger value="billed" data-testid="vc-tab-billed">Billed, not issued ({lists.billed.length})</TabsTrigger>
            <TabsTrigger value="notbilled" data-testid="vc-tab-notbilled">Issued, not billed ({lists.notbilled.length})</TabsTrigger>
            <TabsTrigger value="expiring" data-testid="vc-tab-expiring">Expiring / expired unused ({lists.expiring.length})</TabsTrigger>
          </TabsList>
          <Card className="mt-3">
            <TabsContent value="issued" className="m-0"><IssuedTable rows={lists.issued} /></TabsContent>
            <TabsContent value="available" className="m-0"><AvailableTable rows={lists.available} /></TabsContent>
            <TabsContent value="billed" className="m-0"><BilledTable rows={lists.billed} /></TabsContent>
            <TabsContent value="notbilled" className="m-0"><NotBilledTable rows={lists.notbilled} /></TabsContent>
            <TabsContent value="expiring" className="m-0"><ExpiringTable rows={lists.expiring} /></TabsContent>
          </Card>
        </Tabs>
      </>}
    </div>
  );
}
